/**
 * Types for Tutor Analytics API responses.
 * Field names match the actual API response exactly.
 */

export type TrendPoint = { date: string; overall_avg: number };

/** Communication Tutor (TOPIC) interaction rubric from the server; optional on older docs. */
export type CommunicationRubricScores = {
  initiative?: number;
  responsiveness?: number;
  engagement?: number;
  tamil_use?: number;
};

export interface SpeakingMetricsDoc {
  tenant_id: string;
  student_id: string;
  session_id: string;
  created_at: string;
  duration_seconds: number;
  transcript_excerpt: string;
  scores: {
    pronunciation: number;
    fluency: number;
    grammar: number;
    vocabulary: number;
    overall: number;
  };
  speaking_level: string;
  feedback_ta: string;
  recommendations: string[];
  /** Present for Communication Tutor and newer speaking docs. */
  tutor_type?: string | null;
  rubric_scores?: CommunicationRubricScores | null;
  /** Conversation topic title when provided (e.g. Communication Tutor). */
  topic_label?: string | null;
}

export interface DailyEngagementDoc {
  tenant_id: string;
  student_id: string;
  date: string;
  last_active_at: string;
  sessions: number;
  tutor_minutes: number;
  modes: Record<string, number>;
  tutor_types: Record<string, number>;
  session_ids: string[];
}

export interface TutorSessionDoc {
  tenant_id: string;
  student_id: string;
  session_id: string;
  mode: string;
  tutor_type: string;
  lesson_no: number | null;
  topic_label: string | null;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  turns_user: number;
  turns_model: number;
  status: string;
}

/** Topic shown on speaking evaluations: stored on the doc, or same session in session history. */
export function resolveSpeakingTopicLabel(
  speaking: Pick<SpeakingMetricsDoc, 'session_id' | 'topic_label'>,
  sessionHistory: TutorSessionDoc[],
): string {
  const direct = speaking.topic_label?.trim();
  if (direct) return direct;
  const h = sessionHistory.find((s) => s.session_id === speaking.session_id);
  return (h?.topic_label?.trim() || '') || '';
}

export interface TutorAnalyticsSummary {
  ok: boolean;
  total_tutor_minutes: number;
  sessions_count: number;
  last_active_at: string | null;
  latest_speaking_level: string | null;
  latest_overall_score: number | null;
  trend: TrendPoint[];
  last_lesson_no: number | null;
  last_topic_label: string | null;
  speaking_sessions: SpeakingMetricsDoc[];
  daily_engagement: DailyEngagementDoc[];
  session_history: TutorSessionDoc[];
}